import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './RolesPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');
const STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Roller',
    pageDescription: 'Sistem yonetimi icin rol tanimlari.',
    newRole: 'Yeni rol',
    rolesLoadFailed: 'Roller yuklenemedi.',
    rolesLoadFailedDetail: 'Roller yuklenirken hata olustu.',
    requiredName: 'Rol adi zorunludur.',
    operationFailed: 'Islem basarisiz oldu.',
    roleUpdated: 'Rol guncellendi.',
    roleCreated: 'Rol olusturuldu.',
    operationFailedDetail: 'Islem sirasinda hata olustu.',
    deleteConfirm: 'Bu rolu silmek istediginizden emin misiniz?',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    roleDeleted: 'Rol silindi.',
    deleteFailedDetail: 'Silme sirasinda hata olustu.',
    role: 'Rol',
    description: 'Aciklama',
    noDescription: 'Aciklama yok',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz rol bulunamadi.',
    edit: 'Duzenle',
    delete: 'Sil',
    editRoleAria: 'Rolu duzenle',
    newRoleAria: 'Yeni rol olustur',
    editRoleTitle: 'Rolu duzenle',
    newRoleTitle: 'Yeni rol olustur',
    close: 'Kapat',
    roleName: 'Rol adi',
    shortDescription: 'Kisa aciklama',
    saving: 'Kaydediliyor...',
    update: 'Guncelle',
    save: 'Kaydet',
    cancel: 'Iptal',
    active: 'Aktif',
    passive: 'Pasif'
  },
  'en-US': {
    pageTitle: 'Roles',
    pageDescription: 'Role definitions for system management.',
    newRole: 'New role',
    rolesLoadFailed: 'Roles could not be loaded.',
    rolesLoadFailedDetail: 'An error occurred while loading roles.',
    requiredName: 'Role name is required.',
    operationFailed: 'Operation failed.',
    roleUpdated: 'Role updated.',
    roleCreated: 'Role created.',
    operationFailedDetail: 'An error occurred during the operation.',
    deleteConfirm: 'Are you sure you want to delete this role?',
    deleteFailed: 'Delete operation failed.',
    roleDeleted: 'Role deleted.',
    deleteFailedDetail: 'An error occurred during delete.',
    role: 'Role',
    description: 'Description',
    noDescription: 'No description',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No role found yet.',
    edit: 'Edit',
    delete: 'Delete',
    editRoleAria: 'Edit role',
    newRoleAria: 'Create new role',
    editRoleTitle: 'Edit role',
    newRoleTitle: 'Create new role',
    close: 'Close',
    roleName: 'Role name',
    shortDescription: 'Short description',
    saving: 'Saving...',
    update: 'Update',
    save: 'Save',
    cancel: 'Cancel',
    active: 'Active',
    passive: 'Passive'
  }
};

const createFormState = () => ({
  id: null,
  name: '',
  description: '',
  status: 'Aktif'
});

function RolesPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = (key) => text[key] || UI_TEXT['tr-TR'][key] || key;
  const statusLabel = (value) => (value === 'Aktif' ? t('active') : t('passive'));

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isEditMode = Boolean(formState.id);

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, []);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles`, { headers: buildHeaders() });
      if (!response.ok) {
        throw new Error(text.rolesLoadFailed);
      }
      setRoles(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : text.rolesLoadFailedDetail);
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, text.rolesLoadFailed, text.rolesLoadFailedDetail]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const resetForm = useCallback(() => {
    setFormState(createFormState());
    setFormError('');
  }, []);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const preparePayload = useMemo(() => () => {
    const payload = { name: formState.name.trim(), status: formState.status };
    const description = formState.description.trim();
    if (description) {
      payload.description = description;
    }
    return payload;
  }, [formState.description, formState.name, formState.status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.name) {
      setFormError(t('requiredName'));
      setSaving(false);
      return;
    }

    const method = isEditMode ? 'PUT' : 'POST';
    const url = `${API_BASE_URL}/api/roles${isEditMode ? `/${formState.id}` : ''}`;

    try {
      const response = await fetch(url, {
        method,
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('operationFailed'));
      }
      setSuccess(isEditMode ? t('roleUpdated') : t('roleCreated'));
      closeModal();
      await loadRoles();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationFailedDetail'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (role) => {
    setFormState({
      id: role.id,
      name: role.name || '',
      description: role.description || '',
      status: role.status || 'Aktif'
    });
    setSuccess('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (roleId) => {
    const shouldDelete = window.confirm(t('deleteConfirm'));
    if (!shouldDelete) {
      return;
    }

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/roles/${roleId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('deleteFailed'));
      }
      setSuccess(t('roleDeleted'));
      if (formState.id === roleId) {
        closeModal();
      }
      await loadRoles();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteFailedDetail'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card roles-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={<button type="button" className="btn-secondary" onClick={openCreateModal}>{t('newRole')}</button>}
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="roles-grid">
        <section className="roles-table-panel">
          <div className="table-wrapper">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>{t('role')}</th>
                  <th>{t('description')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="table-placeholder">{t('loading')}</td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={4} className="table-placeholder">{listError}</td>
                  </tr>
                ) : roles.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="table-placeholder">{t('noRecords')}</td>
                  </tr>
                ) : roles.map((role) => {
                  const name = role.name || '-';
                  const description = role.description?.trim() || t('noDescription');
                  const currentStatus = role.status?.trim() || 'Aktif';
                  const statusClass = currentStatus === 'Aktif' ? 'badge badge-active' : 'badge';
                  return (
                    <tr key={role.id || name}>
                      <td>{name}</td>
                      <td>{description}</td>
                      <td><span className={statusClass}>{statusLabel(currentStatus)}</span></td>
                      <td className="action-cell">
                        <button type="button" className="action-icon-btn action-icon-btn-edit" onClick={() => handleEdit(role)} aria-label={t('edit')} title={t('edit')}>
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button type="button" className="action-icon-btn action-icon-btn-delete" onClick={() => handleDelete(role.id)} aria-label={t('delete')} title={t('delete')}>
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editRoleTitle') : t('newRoleTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="name">{t('roleName')}</label>
              <input
                id="name"
                name="name"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder="Admin"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="description">{t('description')}</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formState.description}
                onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
                placeholder={t('shortDescription')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="status">{t('status')}</label>
              <select
                id="status"
                name="status"
                value={formState.status}
                onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{statusLabel(option)}</option>
                ))}
              </select>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('saving') : isEditMode ? t('update') : t('save')}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>{t('cancel')}</button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default RolesPage;
